import React from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus, ShoppingCart } from 'lucide-react';
import { translateMenuTerm, translateMenuTermList } from '@/i18n/translateMenuContent';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { getProductIncludedItems } from '@/lib/productComposition';
import { ProductAttributeIcons } from '@/components/products/ProductAttributeIcons';
import { ProductFavoriteHeart } from '@/components/products/ProductFavoriteHeart';
import { onDemoProductImageError } from '@/lib/demo/demoBusiness';
import { cn } from '@/lib/utils';
import {
  getMenuMutedTextStyle,
  getMenuPrimaryButtonStyle,
  getPremiumModalIconButtonClass,
} from '@/lib/menuTheme';
import { getPublicCatalogDescription } from './catalogHelpers';
import CatalogChoiceGroupsSection from './CatalogChoiceGroupsSection';

export default function CatalogProductModal({
  open,
  onOpenChange,
  selectedProduct,
  bizForTheme,
  standard,
  premium,
  pd,
  pl,
  branding: b,
  accent,
  tc,
  selectedAddOns,
  choiceSelections,
  removedIngredients,
  quantity,
  getAddOnQuantity,
  onAddOnDelta,
  onToggleChoice,
  onToggleIngredient,
  onQuantityChange,
  onAddToCart,
  calculatePrice,
  isFallbackMode,
  regularAddOns = [],
  isLiked = false,
  onToggleLike,
}) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const modalIncluded = selectedProduct
    ? translateMenuTermList(getProductIncludedItems(selectedProduct), lng)
    : [];
  const modalDescPublic = selectedProduct
    ? translateMenuTerm(getPublicCatalogDescription(selectedProduct), lng)
    : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn('max-w-2xl max-h-[90vh] overflow-y-auto', standard && tc.modalSurface)}
        style={
          premium
            ? {
                backgroundColor: b.card_background_color,
                color: b.text_color,
                borderColor: b.card_border_color,
              }
            : undefined
        }
      >
        {selectedProduct && (
          <div className="space-y-6">
            {selectedProduct.photo_url && (
              <div className="relative w-full h-64 rounded-xl overflow-hidden -mt-6">
                <img
                  src={selectedProduct.photo_url}
                  alt={selectedProduct.name}
                  className="w-full h-full object-cover"
                  onError={(e) => onDemoProductImageError(e, bizForTheme)}
                />
                <ProductFavoriteHeart
                  absolute
                  size="lg"
                  liked={isLiked}
                  onToggle={() => onToggleLike?.(selectedProduct.id)}
                />
              </div>
            )}

            {!selectedProduct.photo_url ? (
              <div className="flex items-center gap-2">
                <ProductFavoriteHeart
                  size="md"
                  liked={isLiked}
                  onToggle={() => onToggleLike?.(selectedProduct.id)}
                />
                <span className="text-sm text-muted-foreground">{t('catalog.likeQuestion')}</span>
              </div>
            ) : null}
            {bizForTheme?.is_demo && selectedProduct?.demo_featured ? (
              <Badge className="w-fit bg-orange-600 text-white border-0 shadow-md">{t('catalog.recommended')}</Badge>
            ) : null}
            {bizForTheme?.is_demo ? (
              <p className="text-xs sm:text-sm leading-relaxed rounded-lg border border-border bg-muted/50 px-3 py-2 text-muted-foreground">
                SoluQR puede destacar productos, promos y recomendaciones automáticamente.
              </p>
            ) : null}
            {bizForTheme?.is_demo ? (
              <div className="space-y-2">
                <p
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={premium ? { color: b.text_color } : undefined}
                >
                  Combina bien con
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Papas', 'Bebida', 'Shake'].map((label) => (
                    <span
                      key={label}
                      className="rounded-full border px-3 py-1 text-xs font-medium"
                      style={
                        premium
                          ? {
                              borderColor: b.card_border_color,
                              color: b.text_color,
                              backgroundColor: b.background_color,
                            }
                          : undefined
                      }
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            <DialogHeader className="sr-only">
              <DialogTitle>{selectedProduct.name}</DialogTitle>
            </DialogHeader>

            <div>
              <h2
                className={cn('text-3xl font-bold mb-2', standard && tc.productTitle)}
                style={premium ? { color: b.text_color } : undefined}
              >
                {translateMenuTerm(selectedProduct.name, lng)}
              </h2>
              <ProductAttributeIcons
                attributes={selectedProduct.attributes}
                className="mb-3"
              />
              {modalIncluded.length > 0 && (
                <p className="text-base mb-2">
                  <span
                    className={cn('font-semibold', standard && tc.productTitle)}
                    style={premium ? { color: b.text_color } : undefined}
                  >
                    {t('catalog.comesWith')}{' '}
                  </span>
                  <span
                    className={cn(standard && tc.productMuted)}
                    style={premium ? getMenuMutedTextStyle(bizForTheme) : undefined}
                  >
                    {modalIncluded.join(', ')}
                  </span>
                </p>
              )}
              {modalDescPublic ? (
                <p
                  className={cn(standard && tc.productMuted)}
                  style={premium ? getMenuMutedTextStyle(bizForTheme) : undefined}
                >
                  {modalDescPublic}
                </p>
              ) : null}
            </div>

            <div className="flex items-center gap-3">
              {selectedProduct.discount_percentage > 0 ? (
                <>
                  <span className="text-4xl font-bold" style={{ color: accent }}>
                    ${selectedProduct.final_price?.toLocaleString()}
                  </span>
                  <span
                    className={cn('text-xl line-through', standard && tc.productMuted)}
                    style={premium ? getMenuMutedTextStyle(bizForTheme) : undefined}
                  >
                    ${selectedProduct.price?.toLocaleString()}
                  </span>
                  <Badge variant="destructive">-{selectedProduct.discount_percentage}%</Badge>
                </>
              ) : (
                <span className="text-4xl font-bold" style={{ color: accent }}>
                  ${selectedProduct.price?.toLocaleString()}
                </span>
              )}
            </div>

            <CatalogChoiceGroupsSection
              product={selectedProduct}
              choiceSelections={choiceSelections}
              onToggleChoice={onToggleChoice}
              standard={standard}
              premium={premium}
              branding={b}
              accent={accent}
              tc={tc}
            />

            {Array.isArray(regularAddOns) && regularAddOns.length > 0 && (
              <div className="space-y-3">
                <Label
                  className={cn('text-lg font-semibold', standard && tc.productTitle)}
                  style={premium ? { color: b.text_color } : undefined}
                >
                  {t('catalog.addOns')}
                </Label>
                {regularAddOns.map((addon, index) => {
                  const qty = getAddOnQuantity(addon);
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 border rounded-lg"
                      style={
                        premium
                          ? {
                              borderColor: b.card_border_color,
                              backgroundColor: b.background_color,
                            }
                          : undefined
                      }
                    >
                      <div className="flex-1">
                        <span
                          className={cn('font-medium', standard && tc.productTitle)}
                          style={premium ? { color: b.text_color } : undefined}
                        >
                          {translateMenuTerm(addon.name, lng)}
                        </span>
                        <span
                          className="ml-2"
                          style={premium ? getMenuMutedTextStyle(bizForTheme) : undefined}
                        >
                          +${addon.price?.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className={cn('h-8 w-8', getPremiumModalIconButtonClass(bizForTheme))}
                          style={pd ? { borderColor: accent, color: accent } : undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddOnDelta(addon, -1);
                          }}
                          disabled={qty === 0}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span
                          className={cn('w-8 text-center font-bold', standard && tc.productTitle)}
                          style={premium ? { color: b.text_color } : undefined}
                        >
                          {qty}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className={cn('h-8 w-8', getPremiumModalIconButtonClass(bizForTheme))}
                          style={pd ? { borderColor: accent, color: accent } : undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddOnDelta(addon, 1);
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {Array.isArray(selectedProduct.removable_ingredients) &&
              selectedProduct.removable_ingredients.length > 0 && (
                <div className="space-y-3">
                  <Label
                    className={cn('text-lg font-semibold', standard && tc.productTitle)}
                    style={premium ? { color: b.text_color } : undefined}
                  >
                    {t('catalog.without')}
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedProduct.removable_ingredients.map((ingredient, index) => (
                      <Badge
                        key={index}
                        variant={removedIngredients.includes(ingredient) ? 'default' : 'outline'}
                        className={cn(
                          'cursor-pointer',
                          premium && !removedIngredients.includes(ingredient) && 'hover:opacity-90',
                        )}
                        style={
                          premium && !removedIngredients.includes(ingredient)
                            ? {
                                borderColor: b.card_border_color,
                                color: b.text_color,
                              }
                            : undefined
                        }
                        onClick={() => onToggleIngredient(ingredient)}
                      >
                        {translateMenuTerm(ingredient, lng)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

            <div
              className={cn('flex items-center justify-between p-4 rounded-lg', standard && tc.qtyBar)}
              style={
                premium
                  ? {
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: b.card_border_color,
                      backgroundColor: b.background_color,
                      color: b.text_color,
                    }
                  : undefined
              }
            >
              <span className="font-semibold">{t('catalog.quantity')}</span>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  className={getPremiumModalIconButtonClass(bizForTheme)}
                  style={pd ? { borderColor: accent, color: accent } : undefined}
                  onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="text-xl font-bold w-12 text-center">{quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className={getPremiumModalIconButtonClass(bizForTheme)}
                  style={pd ? { borderColor: accent, color: accent } : undefined}
                  onClick={() => onQuantityChange(quantity + 1)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <Button
              className="w-full h-14 text-lg"
              onClick={onAddToCart}
              disabled={isFallbackMode || selectedProduct.availability === 'out_of_stock'}
              style={
                premium
                  ? {
                      ...getMenuPrimaryButtonStyle(bizForTheme),
                      boxShadow: `0 0 24px -8px ${accent}88`,
                    }
                  : { backgroundColor: accent }
              }
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              {t('catalog.addWithPrice', { price: calculatePrice().toLocaleString() })}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
